import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationSettings } from '~/components/Shared/NotificationProvider/NotificationContext';
import Toggle from '~/components/Shared/Toggle/Toggle';
import InfoIcon from '~/assets/svg/newSvg/info-icon.svg?react';
import styles from './NotificationSettings.module.scss';

const InfoBubble = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className={styles.infoBubble} ref={ref}>
      <button type="button" className={styles.infoBtn} onClick={() => setOpen(v => !v)}>
        <InfoIcon className={styles.infoIcon} />
      </button>
      {open && (
        <div className={styles.infoBubbleContent}>
          {text}
          <div className={styles.infoBubbleArrow} />
        </div>
      )}
    </div>
  );
};

const NotificationSettings = () => {
  const { t } = useTranslation();
  const { settings, updateSettings } = useNotificationSettings();

  return (
    <div className={styles.wrapper}>
      <span className={styles.title}>
        {t('notification_settings', 'Уведомления')}
      </span>

      <div className={styles.container}>
        <div className={styles.section}>
          <span className={styles.sectionTitle}>
            {t('notification_general', 'Общие')}
          </span>

          <div className={styles.rows}>
            <div className={styles.row}>
              <div className={styles.labelGroup}>
                <span className={styles.label}>
                  {t('notification_sound', 'Звуковые уведомления')}
                </span>
                <InfoBubble text={t('notification_sound_desc', 'Воспроизводит звуковой сигнал при поступлении новых заявок, заказов и обращений')} />
              </div>
              <Toggle
                isChecked={settings.soundEnabled}
                onChange={soundEnabled => updateSettings({ soundEnabled })}
              />
            </div>

            <div className={styles.row}>
              <div className={styles.labelGroup}>
                <span className={styles.label}>
                  {t('notification_visual', 'Визуальные уведомления')}
                </span>
                <InfoBubble text={t('notification_visual_desc', 'Показывает всплывающие уведомления на экране при поступлении новых событий')} />
              </div>
              <Toggle
                isChecked={settings.visualEnabled}
                onChange={visualEnabled => updateSettings({ visualEnabled })}
              />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <span className={styles.sectionTitle}>
            {t('notification_sections', 'Разделы')}
          </span>

          <div className={styles.rows}>
            <div className={styles.row}>
              <span className={styles.label}>
                {t('requests', 'Заявки')}
              </span>
              <Toggle
                isChecked={settings.sections.tickets}
                onChange={tickets =>
                  updateSettings({ sections: { ...settings.sections, tickets } })
                }
              />
            </div>

            <div className={styles.row}>
              <span className={styles.label}>
                {t('orders', 'Заказы')}
              </span>
              <Toggle
                isChecked={settings.sections.orders}
                onChange={orders =>
                  updateSettings({ sections: { ...settings.sections, orders } })
                }
              />
            </div>

            <div className={styles.row}>
              <span className={styles.label}>
                {t('feedback', 'Обратная связь')}
              </span>
              <Toggle
                isChecked={settings.sections.feedback}
                onChange={feedback =>
                  updateSettings({ sections: { ...settings.sections, feedback } })
                }
              />
            </div>

            <div className={styles.row}>
              <span className={styles.label}>
                {t('entertainment', 'Досуг')}
              </span>
              <Toggle
                isChecked={settings.sections.entertainment}
                onChange={entertainment =>
                  updateSettings({ sections: { ...settings.sections, entertainment } })
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
